import { ColumnConfig } from '@components/design/migrated/Table';
import { uniq } from 'lodash';
import { ColumnHeader, ProductsCompareKpi, Rank, RankProperty, SaleType } from '@models/Products';
import * as styles from '../products-compare-modal-table/ProductsCompareModalTable.module.scss';
import { TOTAL_ITEM_NAME } from '../Types';

const defaultColumnOptions = {
  compact: true,
  className: styles.ProductsCompareModalTable__Header,
};

const defaultColumns: ColumnConfig[] = [
  {
    headerKey: ColumnHeader.itemName,
    name: '',
    compact: true,
    className: styles.ProductsCompareModalTable__FirstHeader,
  },
  {
    headerKey: 'total',
    name: 'Total',
    ...defaultColumnOptions,
  },
];

/**
 * This function creates an array of ColumnConfig objects based on the productsCompareKpi object.
 * The array of ColumnConfig objects will be used to populate the products compare models table columns.
 * The ColumnConfig[] will include a Total column and the selected models.
 *
 * @param productsCompareKpi
 * @returns ColumnConfig[] used to populate the products compare model table columns
 */
export const getModelNamesColumn: (productsCompareKpi: ProductsCompareKpi) => ColumnConfig[] = (productsCompareKpi) => [
  ...defaultColumns,
  ...productsCompareKpi.items
    .filter((kpi) => kpi.item.itemName !== TOTAL_ITEM_NAME)
    .map((kpi) => kpi.item)
    .map((item) => ({
      headerKey: item.itemId,
      name: `${item.itemBrand} ${item.itemName}`,
      ...defaultColumnOptions,
    })),
];

/**
 * This function returns an array of first activity dates of the selected models.
 *
 * @param productsCompareKpi
 * @returns an array of first activity dates
 */
export const getFirstActivityRow: (productsCompareKpi: ProductsCompareKpi) => string[] = (productsCompareKpi) =>
  productsCompareKpi.items.map((kpi) => kpi.item.firstActivityDate);

/**
 * This function returns all the unique feature names of the selected models, sorted in alphabetical order.
 *
 * @param productsCompareKpi
 * @returns an array of unique feature names, sorted alphabetically.
 */
export const getFeatureNames: (productsCompareKpi: ProductsCompareKpi) => string[] = (productsCompareKpi) =>
  uniq(
    productsCompareKpi.itemFeatures
      .flatMap((itemFeature) => itemFeature.features)
      .filter((feature) => !!feature.name)
      .map((feature) => feature.name!)
      .sort((featName1, featName2) => featName1.localeCompare(featName2)),
  );

/**
 * This is a helper function that groups each feature name to its feature value as a pair
 * for each feature, and then return a map of item ID => a map of the feature pairs.
 *
 * @param productsCompareKpi
 * @returns a map of itemId => a map of feature name => feature value
 */
export const getProcessedFeatures = (productsCompareKpi: ProductsCompareKpi) => {
  const productFeatures = productsCompareKpi.itemFeatures.map((itemFeature) => {
    const featurePairs = itemFeature.features.map(({ name, value }) => [name, value] as const);
    return [itemFeature.itemId, new Map(featurePairs)] as const;
  });
  return new Map(productFeatures);
};

/**
 * This is a type guard that checks if the parameter is a Rank object, and it also supports type narrowing.
 *
 * @param data
 * @returns boolean of whether data is a Rank object
 */
export const isRank = (data: any): data is Rank =>
  RankProperty.rank in data && RankProperty.change in data && RankProperty.position in data;

/**
 * get Sales Type (Revenue or Units) based on headerKey from pages config sorting options for products
 * @param headerKey
 */
export const getSalesType = (headerKey: string) => {
  return headerKey.toLowerCase().includes('units') ? SaleType.volume : SaleType.revenue;
};
